import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import DotsLoader from "../../../components/icons/DotsLoader";
import EmptyState from "../../../components/shared/EmptyState";
import ThreeDotsLoader from "../../../components/shared/ThreeDotsLoader";
import useWindowSize from "../../../hooks/useWindowSize";
import ActorCard, { Actor } from "./ActorCard";
import ActorCardHorizontal from "./ActorCardHorizontal";
import ViewSwitcher, { SearchViewMode } from "../MoviesList/ViewSwitcher";
import styles from "./ActorsList.module.scss";

interface ActorsListProps {
  data: Actor[];
  error: any;
  isFetching?: boolean;
  isLastData?: boolean;
  favoritesList?: number[];
  favouritesHandler?: (e: any, actor: Actor, isFav: boolean) => void;
  isFavoritePage?: boolean;
  setPageHandler: () => void;
  totalCount?: number;
  enableViewSwitcher?: boolean;
  viewStorageKey?: string;
}

const getSavedViewMode = (storageKey: string | undefined, defaultViewMode: SearchViewMode): SearchViewMode => {
  if (!storageKey) return defaultViewMode;

  try {
    const savedViewMode = localStorage.getItem(storageKey);
    return savedViewMode === "grid" || savedViewMode === "list" ? savedViewMode : defaultViewMode;
  } catch (e) {
    return defaultViewMode;
  }
};

const ActorsList = ({
  data,
  error,
  isFetching,
  isLastData,
  favoritesList,
  favouritesHandler,
  isFavoritePage,
  setPageHandler,
  totalCount,
  enableViewSwitcher = false,
  viewStorageKey,
}: ActorsListProps) => {
  const paginationAnchorRef = useRef(null);
  const { t } = useTranslation();
  const { windowSize } = useWindowSize();
  const isMobile = windowSize === "mobile";
  const defaultViewMode: SearchViewMode = isFavoritePage ? "list" : "grid";
  const [activeView, setActiveView] = useState<SearchViewMode>(() =>
    getSavedViewMode(viewStorageKey, defaultViewMode),
  );
  const viewMode = isMobile ? defaultViewMode : enableViewSwitcher ? activeView : defaultViewMode;

  useEffect(() => {
    setActiveView(getSavedViewMode(viewStorageKey, defaultViewMode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewStorageKey]);

  const setViewModeHandler = (nextViewMode: SearchViewMode) => {
    setActiveView(nextViewMode);

    if (!viewStorageKey) return;

    try {
      localStorage.setItem(viewStorageKey, nextViewMode);
    } catch (e) {
      return;
    }
  };

  useEffect(() => {
    if (isFetching || isLastData) return;

    const elem = paginationAnchorRef.current;
    const handleIntersection = (entries: any) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting && data.length && !error) {
          setPageHandler();
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      threshold: 0.1,
    });

    if (elem) {
      observer.observe(elem);
    }

    return () => {
      if (elem) {
        observer.unobserve(elem);
      }
    };
  }, [setPageHandler, isFetching, isLastData, data?.length, error]);

  const cardListClassName = `${styles.cardListWrapper} ${
    isFavoritePage
      ? viewMode === "list"
        ? styles.favoriteCardListWrapper
        : styles.favoriteGridCardListWrapper
      : viewMode === "list"
        ? styles.listCardListWrapper
        : ""
  }`;

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.headerWrapper}>
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}>{t("actors")}</h2>
          {isFavoritePage && <span className={styles.counter}>{totalCount ?? data.length}</span>}
        </div>
        {enableViewSwitcher && !isMobile && (
          <ViewSwitcher activeView={activeView} onChange={setViewModeHandler} />
        )}
      </div>

      {isFetching && !data.length ? (
        <div className={styles.loaderWrapper}>
          <DotsLoader />
        </div>
      ) : !data.length ? (
        <EmptyState
          className={styles.emptyWrapper}
          variant={isFavoritePage ? "favorites" : "search"}
          text={t(isFavoritePage ? "emptyFavourites" : "emptySearch")}
        />
      ) : (
        <>
          <div className={cardListClassName}>
            {data.map((actor) =>
              viewMode === "list" ? (
                <ActorCardHorizontal
                  key={actor.id}
                  actor={actor}
                  isFav={Boolean(favoritesList?.includes(actor.id))}
                  favouritesHandler={favouritesHandler}
                />
              ) : (
                <ActorCard
                  key={actor.id}
                  actor={actor}
                  isFav={Boolean(favoritesList?.includes(actor.id))}
                  favouritesHandler={favouritesHandler}
                />
              ),
            )}
          </div>
          {!isLastData && (
            <div className={styles.loaderWrapper} ref={paginationAnchorRef}>
              <ThreeDotsLoader />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ActorsList;
